// The Studio singleton tab's centre: the canvas of the project the author is
// in (Studio v1 D1; `frame.studioProject`, `/projects/:id`). Projects the
// cards on that project's canvas from the Library the shell already holds
// and maps the canvas's intents onto the shell's existing doors:
//  - selection IS the composer's context: the selected cards are the
//    composer's mention chips (the same `@`-mention path, so removing a chip
//    deselects its card, and the next message carries them);
//  - the toolbar (pen B4): the container tool puts cards into a new module
//    (or a card menu adds them to one) and a new material starts empty, each
//    one command (useStudioCommands.ts) whose result is placed where it was
//    made; Source uploads; Menu shows or hides the app's left menu;
//  - opening anything lifts it open on the desk (studioDesk.ts): a module
//    (binder) or course (shelf) over its spot, a document zoomed in with
//    its middle by kind (StudioDocument.tsx); the Studio never leads to the
//    Library; what is open is kept per device;
//  - a container's part rows move a part, or take it out, each with an Undo
//    (useStudioCommands.ts);
//  - briefs (decisions 2 and 3): an open module or course plans a document
//    with its brief (or a module), an open document's brief is changed and
//    generated from, and each open container's foot shows its export
//    readiness (useDeskReadiness.ts) with "Generate it";
//  - a container just made is named in place: the field shows until the
//    name is kept (the existing rename) or the default is;
//  - a running assistant turn is a working card (studioWork.ts), next to the
//    cards its message referenced; what it makes appears as the Library
//    refreshes (the shell's log-head heartbeat, the same live path the
//    Library uses);
//  - a card taken off the canvas stays in the Library (useStudioCommands.ts,
//    with an Undo); the canvas menu puts any Library item not on this
//    canvas onto it; "Delete material …" asks first, through the Library's
//    own confirmation;
//  - which containers are folded is this person's, kept on this device;
//  - the view (zoom + centre) is remembered on this device between visits;
//  - where each card lies is the project's (placed, moved and taken off by
//    project commands, so it is shared state); a card just dropped keeps
//    its new place until the project's layout shows it (view state);
//  - with no project chosen (a bare `/studio`), a way to all projects.

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Button } from "../../../components/Button/Button.js";
import type { ComposerResourceRef } from "../../../components/Composer/PowerComposer/types.js";
import { EmptyState } from "../../../components/EmptyState/EmptyState.js";
import type { PartIntent } from "../../../components/ModuleCard/partOps.js";
import { Studio } from "../../../components/Studio/Studio.js";
import {
    type ContainerCard,
    projectStudioCards,
} from "../../../components/Studio/studioCards.js";
import {
    type Point,
    placeCards,
    type StudioLayout,
} from "../../../components/Studio/studioLayout.js";
import { projectStudioWork } from "../../../components/Studio/studioWork.js";
import {
    readContainerKind,
    readFoldedContainers,
    readStudioView,
    writeContainerKind,
    writeFoldedContainers,
    writeStudioView,
} from "../../studioLayoutPreference.js";
import { toComposerResourceRef } from "../constants.js";
import { useAppShell } from "../context.js";
import { StudioDocument } from "./StudioDocument.js";
import { useDeskReadiness } from "./useDeskReadiness.js";

export const StudioCentre = (): ReactNode => {
    const { t } = useLingui();
    const {
        library,
        searchResults,
        composerMentions,
        handleMentionsChange,
        refFor,
        dispatch,
        frame,
        health,
        studio,
        openFilePicker,
        chatState,
        turnFailureText,
        openConversation,
        submitText,
        openOverlay,
        submitResourceRename,
        setConfirmDelete,
        ports,
    } = useAppShell();
    const readiness = useDeskReadiness(ports, studio.desk, library);
    // Where a card was just dropped, until the project's layout shows it
    // (view state; the project's place wins once it differs no more).
    const [dropped, setDropped] = useState<ReadonlyMap<string, Point>>(
        new Map(),
    );
    // The view this person left the Studio at (zoom + centre), per device.
    const [storedView] = useState(readStudioView);
    // The Container tool's kind, remembered per device (pen B4).
    const [containerKind, setContainerKind] = useState(readContainerKind);
    // The container just made, its name being typed (view state: a reload
    // keeps the default name).
    const [naming, setNaming] = useState<string | null>(null);
    // The containers this person folded, per device; only they fold or
    // unfold one (nothing folds itself).
    const [folded, setFolded] = useState(readFoldedContainers);

    // The project the author is in, and where each card lies on its canvas.
    const project = (library?.projects ?? []).find(
        (p) => p.id === frame.studioProject,
    );
    const canvas = new Map(
        (project?.canvas ?? []).map((item): [string, Point] => [
            item.target,
            { x: item.x, y: item.y },
        ]),
    );
    const layout: StudioLayout = {
        positions: new Map([...canvas, ...dropped]),
    };

    // Every card, the ones on this canvas, and the rest (the canvas menu
    // puts one on).
    const all = projectStudioCards(
        searchResults,
        library,
        new Set(canvas.keys()),
    );
    const cards = all.filter((c) => canvas.has(c.id));
    const offCanvas = all
        .filter((c) => !canvas.has(c.id))
        .map((c) => ({ id: c.id, title: c.title }));
    const placements = placeCards(cards, layout);
    const onCanvas = new Set(cards.map((c) => c.id));
    const selected = new Set(
        composerMentions.map((m) => m.id).filter((id) => onCanvas.has(id)),
    );
    // The mentions that are not cards (an `@`-picked package) stay put.
    const others = composerMentions.filter((m) => !onCanvas.has(m.id));
    const chipOf = (id: string): readonly ComposerResourceRef[] => {
        const ref = refFor(id);
        return ref === undefined ? [] : [toComposerResourceRef(ref)];
    };
    const selectedChips = composerMentions.filter((m) => selected.has(m.id));

    const place = (id: string, at: Point) => {
        setDropped((m) => new Map(m).set(id, at));
        studio.moveOnCanvas(id, at);
    };
    // A card onto this canvas (a new one, or one picked from the menu).
    const placeNew = (id: string, at: Point) => studio.placeOnCanvas(id, at);

    // A container just made: placed, then named in place.
    const made = (at: Point) => (id: string) => {
        placeNew(id, at);
        setNaming(id);
    };
    const onPartIntent = (container: ContainerCard, intent: PartIntent) => {
        switch (intent.type) {
            // Opened on the desk by the canvas itself (Studio.tsx).
            case "open":
                return;
            case "move":
                studio.movePart(
                    container.id,
                    intent.part,
                    intent.from,
                    intent.to,
                );
                return;
            case "remove":
                studio.removePart(
                    container.id,
                    container.kind,
                    intent.part,
                    intent.at,
                );
                return;
            default:
                exhaustSwitchCase(intent);
        }
    };

    // No project chosen (or it is gone): the way to all projects.
    if (library !== null && project === undefined)
        return (
            <EmptyState
                title={t`No project open`}
                body={t`Open a project to work on its canvas.`}
            >
                <Button
                    variant="primary"
                    onClick={() => dispatch({ type: "open-projects" })}
                >
                    {t`All projects`}
                </Button>
            </EmptyState>
        );

    return (
        <Studio
            cards={cards}
            placements={placements}
            selected={selected}
            projectTitle={project?.title ?? ""}
            onAllProjects={() => dispatch({ type: "open-projects" })}
            loading={library === null}
            onSelectOnly={(id) =>
                handleMentionsChange([...others, ...chipOf(id)])
            }
            onToggleSelected={(id) =>
                handleMentionsChange(
                    selected.has(id)
                        ? composerMentions.filter((m) => m.id !== id)
                        : [...composerMentions, ...chipOf(id)],
                )
            }
            onClearSelection={() => {
                if (selectedChips.length > 0) handleMentionsChange(others);
            }}
            // The same export sheet as the material's own Export, over the
            // canvas.
            onExport={(card) =>
                openOverlay({ type: "export", materialId: card.id })
            }
            onMove={place}
            onPutInNewModule={(ids, at) => studio.putInNewModule(ids, made(at))}
            onPutInNewCourse={(ids, at) => studio.putInNewCourse(ids, made(at))}
            onAddToContainer={studio.addToModule}
            onPartIntent={onPartIntent}
            naming={naming}
            onNamed={(id, title) => {
                setNaming(null);
                if (title !== null) submitResourceRename(id, title);
            }}
            folded={folded}
            onToggleFold={(id) => {
                const next = folded.has(id)
                    ? new Set([...folded].filter((f) => f !== id))
                    : new Set([...folded, id]);
                setFolded(next);
                writeFoldedContainers(next);
            }}
            onRemoveFromCanvas={studio.removeFromCanvas}
            offCanvas={offCanvas}
            onPutBack={placeNew}
            // The same confirmation as the Library's Delete.
            onDelete={(card) => {
                const ref = refFor(card.id);
                if (ref !== undefined) setConfirmDelete(ref);
            }}
            revealed={studio.revealed}
            containerKind={containerKind}
            onContainerKindChange={(kind) => {
                setContainerKind(kind);
                writeContainerKind(kind);
            }}
            onUpload={openFilePicker}
            onNewMaterial={(kind, at) =>
                studio.newMaterial(kind, (id) => placeNew(id, at))
            }
            menuShown={frame.nav === "expanded"}
            onToggleMenu={() => dispatch({ type: "toggle-rail" })}
            online={health === "online"}
            // The assistant's turns as working cards (the chat's own running
            // state); what they make arrives with the Library's live refresh.
            work={projectStudioWork(
                chatState.sessions,
                placements,
                turnFailureText,
            )}
            onOpenWork={openConversation}
            onAsk={(text) => submitText(text)}
            storedView={storedView}
            onLeave={writeStudioView}
            desk={studio.desk}
            onDesk={studio.onDesk}
            renderDocument={(doc, front) => (
                <StudioDocument doc={doc} front={front} />
            )}
            briefs={{
                readiness,
                notes: studio.notes,
                onPlanDocument: studio.planDocument,
                onPlanModule: studio.planModule,
                onReviseBrief: studio.reviseBrief,
                onGenerate: studio.generate,
                onGenerateAll: studio.generateAll,
            }}
        />
    );
};
